import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Skeleton from "../components/Skeleton";
import { confirmAction } from "../components/ConfirmationModal";
import {
  createCategory,
  createSubcategory,
  deleteCategory,
  deleteSubcategory,
  listCategories,
  listSubcategories,
  updateCategory,
  updateSubcategory,
} from "../services/adminDataService";

const emptyForm = {
  name: "",
  description: "",
  isActive: true,
};

const getItemId = (item) => item?.id ?? item?._id;

const getEntityImageUrl = (item) =>
  item?.imgUrl ||
  item?.imageUrl ||
  item?.image ||
  item?.thumbnail ||
  item?.icon ||
  "";

const getSubcategoriesFromCategory = (category) => {
  if (Array.isArray(category?.subcategories)) return category.subcategories;
  if (Array.isArray(category?.subCategories)) return category.subCategories;
  if (Array.isArray(category?.children)) return category.children;

  return [];
};

const EntityModal = ({
  show,
  title,
  submitLabel,
  type,
  categories,
  selectedCategoryId,
  formData,
  errors,
  saving,
	  onChange,
	  onSelectedCategoryChange,
  onClose,
  onSubmit,
}) => {
  if (!show) return null;

  return (
    <>
      <div className="modal show healthid_request" style={{ display: "block" }} tabIndex="-1" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header d-flex align-items-center justify-content-between">
              <h5 className="modal-title">{title}</h5>
              <button type="button" className="close-btns" onClick={onClose}>
                x
              </button>
            </div>
            <div className="modal-body">
              <form className="request-order-form" onSubmit={onSubmit}>
                <div className="row g-3">
                  <div className="col-lg-12">
                    <label className="form-label" htmlFor="settingsName">Name</label>
                    <input
                      id="settingsName"
                      name="name"
                      className={`form-control bg-img-none ${errors.name ? "is-invalid" : ""}`}
                      value={formData.name}
                      onChange={onChange}
                    />
                    {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                  </div>
                  {type === "subcategory" && (
                    <div className="col-lg-12">
                      <label className="form-label" htmlFor="settingsParentCategory">Parent Category</label>
                      <select
                        id="settingsParentCategory"
                        className="form-control bg-img-none"
                        value={selectedCategoryId}
                        onChange={onSelectedCategoryChange}
                      >
                        <option value="">Select category</option>
                        {categories.map((category) => (
                          <option key={getItemId(category)} value={getItemId(category)}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
	                  <div className="col-lg-12">
	                    <label className="form-label" htmlFor="settingsDescription">Description</label>
                    <textarea
                      id="settingsDescription"
                      name="description"
                      rows="3"
                      className="form-control bg-img-none"
                      value={formData.description}
                      onChange={onChange}
	                    />
	                  </div>
	                  <div className="col-lg-12">
                    <label className="recommendation-toggle">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={onChange}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                  {errors.form && (
                    <div className="col-12">
                      <div className="alert alert-danger mb-0">{errors.form}</div>
                    </div>
                  )}
                  <div className="col-12">
                    <button type="submit" className="btn btn-primary w-100" disabled={saving}>
                      {saving ? "Saving..." : submitLabel}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
    </>
  );
};

const Settings = () => {
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [subcategorySearch, setSubcategorySearch] = useState("");
  const [categoryStatus, setCategoryStatus] = useState("");
  const [subcategoryStatus, setSubcategoryStatus] = useState("");
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [saving, setSaving] = useState(false);
  const [, setError] = useState("");
  const [, setMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  const selectedCategory = useMemo(
    () => categories.find((category) => String(getItemId(category)) === String(selectedCategoryId)),
    [categories, selectedCategoryId],
  );

  const activeCategories = useMemo(
    () => categories.filter((category) => category.isActive !== false).length,
    [categories],
  );

  const activeSubcategories = useMemo(
    () => subcategories.filter((subcategory) => subcategory.isActive !== false).length,
    [subcategories],
  );

  const loadCategories = async (filters = { search: categorySearch, isActive: categoryStatus }) => {
    try {
      setLoadingCategories(true);
      setError("");
      const nextCategories = await listCategories({ page: 1, limit: 100, ...filters });
      setCategories(nextCategories);

      const currentExists = nextCategories.some(
        (category) => String(getItemId(category)) === String(selectedCategoryId),
      );

      if (!currentExists) {
        setSelectedCategoryId(getItemId(nextCategories[0]) || "");
      }
    } catch (loadError) {
      setError(loadError.message || "Unable to load categories.");
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadSubcategories = async (
    categoryId = selectedCategoryId,
    filters = { search: subcategorySearch, isActive: subcategoryStatus },
  ) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    try {
      setLoadingSubcategories(true);
      setError("");
      const fallbackSubcategories = getSubcategoriesFromCategory(
        categories.find((category) => String(getItemId(category)) === String(categoryId)),
      );
      const nextSubcategories = await listSubcategories(categoryId, {
        page: 1,
        limit: 100,
        ...filters,
      });
      setSubcategories(nextSubcategories.length ? nextSubcategories : fallbackSubcategories);
    } catch (loadError) {
      const fallbackSubcategories = getSubcategoriesFromCategory(
        categories.find((category) => String(getItemId(category)) === String(categoryId)),
      );
      if (fallbackSubcategories.length) {
        setSubcategories(fallbackSubcategories);
        return;
      }
      setError(loadError.message || "Unable to load subcategories.");
    } finally {
      setLoadingSubcategories(false);
    }
  };

  useEffect(() => {
    loadCategories({ search: "", isActive: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname === "/settings/categories") return;
    loadSubcategories(selectedCategoryId, { search: "", isActive: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId, location.pathname]);

  useEffect(() => {
    const handlePageAction = (event) => {
      if (event.detail?.path !== "/settings/categories" || event.detail?.action !== "create") return;
      setModalType("category");
      setEditingItem(null);
      setFormData({ ...emptyForm });
      setFormErrors({});
    };
    window.addEventListener("admin-page-action", handlePageAction);
    return () => window.removeEventListener("admin-page-action", handlePageAction);
  }, []);

  useEffect(() => {
    const handlePageSearch = (event) => {
      if (event.detail?.path !== "/settings/categories") return;

      const search = event.detail.query || "";
      setCategorySearch(search);
      loadCategories({ search, isActive: "" });
    };

    window.addEventListener("admin-page-search", handlePageSearch);
    return () => window.removeEventListener("admin-page-search", handlePageSearch);
    // The header event supplies the current query, so this listener only needs to mount once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

	  const openCreate = (type) => {
	    if (type === "subcategory" && !selectedCategoryId && categories.length > 0) {
	      setSelectedCategoryId(getItemId(categories[0]));
	    }
	    setModalType(type);
	    setEditingItem(null);
	    setFormData({ ...emptyForm });
	    setFormErrors({});
	  };

  const openEdit = (type, item) => {
    setModalType(type);
    setEditingItem(item);
	    setFormData({
	      name: item.name || "",
	      description: item.description || "",
	      isActive: item.isActive !== false,
	    });
	    setFormErrors({});
	  };

  const closeModal = () => {
	    setModalType("");
	    setEditingItem(null);
	    setFormData({ ...emptyForm });
	    setFormErrors({});
	  };

	  const handleChange = (event) => {
	    const { name, value, checked, type } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
	    setFormErrors((previous) => ({ ...previous, [name]: "", form: "" }));
	  };

  const handleParentCategoryChange = (event) => {
    setSelectedCategoryId(event.target.value);
    setFormErrors((previous) => ({ ...previous, form: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = "Name is required.";
    if (modalType === "subcategory" && !selectedCategoryId) {
      nextErrors.form = "Select a category before adding a subcategory.";
    }
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

	  const buildPayload = () => ({
	    name: formData.name.trim(),
	    description: formData.description.trim(),
	    isActive: formData.isActive,
	  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      setMessage("");

      if (modalType === "category") {
        if (editingItem) {
          await updateCategory(getItemId(editingItem), buildPayload());
          setMessage("Category updated successfully.");
        } else {
          await createCategory(buildPayload());
          setMessage("Category created successfully.");
        }
        closeModal();
        await loadCategories();
        return;
      }

      if (editingItem) {
        await updateSubcategory(selectedCategoryId, getItemId(editingItem), buildPayload());
        setMessage("Subcategory updated successfully.");
      } else {
        await createSubcategory(selectedCategoryId, buildPayload());
        setMessage("Subcategory created successfully.");
      }
      closeModal();
      await loadSubcategories();
    } catch (saveError) {
      setFormErrors({ form: saveError.message || "Unable to save settings." });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!await confirmAction({ title: "Delete category?", message: `Delete ${category.name}? This cannot be undone.`, confirmLabel: "Delete", destructive: true })) return;

    try {
      setMessage("");
      await deleteCategory(getItemId(category));
      setMessage("Category deleted successfully.");
      await loadCategories();
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete category.");
    }
  };

  const handleDeleteSubcategory = async (subcategory) => {
    if (!await confirmAction({ title: "Delete subcategory?", message: `Delete ${subcategory.name}? This cannot be undone.`, confirmLabel: "Delete", destructive: true })) return;

    try {
      setMessage("");
      await deleteSubcategory(selectedCategoryId, getItemId(subcategory));
      setMessage("Subcategory deleted successfully.");
      await loadSubcategories();
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete subcategory.");
    }
  };

  const handleCategorySearch = (event) => {
    event.preventDefault();
    loadCategories({ search: categorySearch, isActive: categoryStatus });
  };

  const handleSubcategorySearch = (event) => {
    event.preventDefault();
    loadSubcategories(selectedCategoryId, {
      search: subcategorySearch,
      isActive: subcategoryStatus,
    });
  };

  const modalTitle =
    modalType === "category"
      ? editingItem
        ? "Update Category"
        : "Add Category"
      : editingItem
        ? "Update Subcategory"
        : "Add Subcategory";

  if (location.pathname === "/settings/categories") {
    const tileColors = ["#fde2d8", "#d9f4f5", "#f9dfe6", "#eee1fa", "#fff2c7", "#e0efff", "#e8f4d8", "#fde9dc"];
    return <div className="main-wrapper categories-shell"><div className="page-wrapper"><main className="page-content categories-page">
      <section className="categories-list">
        {loadingCategories && [1, 2, 3, 4].map((item) => <div className="category-list-row" key={item}><Skeleton width="100%" height="84px" /></div>)}
        {!loadingCategories && categories.length === 0 && <div className="categories-empty">No categories found.</div>}
        {!loadingCategories && categories.map((category, index) => <article className="category-list-row" key={getItemId(category)}>
          <div className="category-list-tile" style={{ background: tileColors[index % tileColors.length] }}><span>{category.name || "Category"}</span></div>
          <strong>{category.name || "Unnamed category"}</strong>
          <div className="category-list-actions"><button type="button" onClick={() => openEdit("category", category)} aria-label={`Edit ${category.name || "category"}`} title="Edit category"><i className="bi bi-pencil-square" /></button><button type="button" onClick={() => handleDeleteCategory(category)} aria-label={`Delete ${category.name || "category"}`} title="Delete category"><i className="bi bi-trash3" /></button></div>
        </article>)}
      </section>
    </main></div><EntityModal show={Boolean(modalType)} title={modalTitle} type={modalType} categories={categories} selectedCategoryId={selectedCategoryId} submitLabel={editingItem ? "Save Changes" : "Add Category"} formData={formData} errors={formErrors} saving={saving} onChange={handleChange} onSelectedCategoryChange={handleParentCategoryChange} onClose={closeModal} onSubmit={handleSubmit} /></div>;
  }

  return (
    <div className="main-wrapper">
      <div className="page-wrapper">
        <div className="page-content">
          <div className="row g-4">
            <div className="col-12">
              <div className="card rounded-4">
                <div className="section-title p-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <h5>Settings</h5>
                    <p className="small text-muted mb-0">
                      Manage medicine categories and subcategories
                    </p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="settings-summary-grid">
                    <div className="settings-summary-item">
                      <span>Categories</span>
                      <strong>{categories.length}</strong>
                      <small>{activeCategories} active</small>
                    </div>
                    <div className="settings-summary-item">
                      <span>Selected category</span>
                      <strong>{selectedCategory?.name || "-"}</strong>
                      <small>
                        {selectedCategoryId ? `ID ${selectedCategoryId} · ${activeSubcategories} active subcategories` : "Choose a category"}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card rounded-4 h-100">
                <div className="section-title p-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <h5>Categories</h5>
                    <p className="small text-muted mb-0">
                      {categories.length} total · {activeCategories} active
                    </p>
                  </div>
                  <button type="button" onClick={() => openCreate("category")} className="btn btn-dark">
                    Add Category
                  </button>
                </div>
                <div className="p-4">
                  <form className="admin-filter-bar settings-filter-bar mb-4" onSubmit={handleCategorySearch}>
                    <input
                      className="form-control"
                      placeholder="Search categories..."
                      value={categorySearch}
                      onChange={(event) => setCategorySearch(event.target.value)}
                    />
                    <select
                      className="form-control"
                      value={categoryStatus}
                      onChange={(event) => setCategoryStatus(event.target.value)}
                    >
                      <option value="">All status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                    <button className="btn btn-primary" type="submit">Search</button>
                  </form>
                  <div className="settings-entity-list">
                    {loadingCategories &&
                      [1, 2, 3].map((item) => (
                        <div className="settings-entity-card" key={item}>
                          <Skeleton width="100%" height="4.5rem" />
                        </div>
                      ))}

                    {!loadingCategories && categories.length === 0 && (
                      <div className="settings-empty-state">No categories found.</div>
                    )}

                    {!loadingCategories &&
                      categories.map((category, index) => {
                          const categoryId = getItemId(category);
                          const selected = String(categoryId) === String(selectedCategoryId);
                          const childCount =
                            category.subcategoryCount ??
                            category.subcategoriesCount ??
                            getSubcategoriesFromCategory(category).length;

                          return (
                            <div
                              key={categoryId}
                              className={`settings-entity-card ${selected ? "is-selected" : ""}`}
                            >
                              <div
                                role="button"
                                tabIndex={0}
                                className="settings-entity-main"
                                onClick={() => setSelectedCategoryId(categoryId)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    setSelectedCategoryId(categoryId);
                                  }
                                }}
                              >
                                <div className="settings-table-image">
                                  {getEntityImageUrl(category) ? (
                                    <img src={getEntityImageUrl(category)} alt={category.name || "Category"} />
                                  ) : (
                                    <span>No image</span>
                                  )}
                                </div>
                                <div className="settings-entity-copy">
                                  <span className="settings-entity-index">#{index + 1}</span>
                                  <strong>{category.name || "-"}</strong>
                                  <p>{category.description || "No description"}</p>
                                </div>
                              </div>

                              <div className="settings-entity-meta">
                                <span className={`recommendation-status ${category.isActive === false ? "is-inactive" : ""}`}>
                                  {category.isActive === false ? "Inactive" : "Active"}
                                </span>
                                <button
                                  type="button"
                                  className={`settings-manage-button ${selected ? "is-selected" : ""}`}
                                  onClick={() => setSelectedCategoryId(categoryId)}
                                >
                                  {selected ? "Managing" : "Manage"} {childCount ? `(${childCount})` : ""}
                                </button>
                              </div>

                              <div className="settings-entity-actions">
                                <button type="button" onClick={() => openEdit("category", category)} className="edit-icon">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/></svg>
                                </button>
                                <button type="button" className="delete" onClick={() => handleDeleteCategory(category)}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card rounded-4 h-100">
                <div className="section-title p-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <h5>Subcategories</h5>
                    <p className="small text-muted mb-0">
                      {selectedCategory?.name || "Select a category"} · {subcategories.length} listed
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => openCreate("subcategory")}
                    className="btn btn-dark"
                    disabled={!selectedCategoryId}
                  >
                    Add Subcategory
                  </button>
                </div>
                <div className="p-4">
                  <div className="settings-parent-bar mb-4">
                    <div>
                      <span>Parent category</span>
                      <strong>{selectedCategory?.name || "No category selected"}</strong>
                      <small>
                        {selectedCategoryId
                          ? `New subcategories will be created under category ID ${selectedCategoryId}.`
                          : "Select a category from the left table first."}
                      </small>
                    </div>
                    <select
                      className="form-control"
                      value={selectedCategoryId}
                      onChange={(event) => setSelectedCategoryId(event.target.value)}
                    >
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={getItemId(category)} value={getItemId(category)}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <form className="admin-filter-bar settings-filter-bar mb-4" onSubmit={handleSubcategorySearch}>
                    <input
                      className="form-control"
                      placeholder="Search subcategories..."
                      value={subcategorySearch}
                      onChange={(event) => setSubcategorySearch(event.target.value)}
                      disabled={!selectedCategoryId}
                    />
                    <select
                      className="form-control"
                      value={subcategoryStatus}
                      onChange={(event) => setSubcategoryStatus(event.target.value)}
                      disabled={!selectedCategoryId}
                    >
                      <option value="">All status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                    <button className="btn btn-primary" type="submit" disabled={!selectedCategoryId}>
                      Search
                    </button>
                  </form>
                  <div className="settings-entity-list">
                    {loadingSubcategories &&
                      [1, 2].map((item) => (
                        <div className="settings-entity-card" key={item}>
                          <Skeleton width="100%" height="4.5rem" />
                        </div>
                      ))}

                    {!loadingSubcategories && !selectedCategoryId && (
                      <div className="settings-empty-state">Select a category first.</div>
                    )}

                    {!loadingSubcategories && selectedCategoryId && subcategories.length === 0 && (
                      <div className="settings-empty-state">No subcategories found.</div>
                    )}

                    {!loadingSubcategories && subcategories.map((subcategory, index) => (
                      <div className="settings-entity-card" key={getItemId(subcategory)}>
                        <div className="settings-entity-main">
                          <div className="settings-table-image">
                            {getEntityImageUrl(subcategory) ? (
                              <img src={getEntityImageUrl(subcategory)} alt={subcategory.name || "Subcategory"} />
                            ) : (
                              <span>No image</span>
                            )}
                          </div>
                          <div className="settings-entity-copy">
                            <span className="settings-entity-index">#{index + 1}</span>
                            <strong>{subcategory.name || "-"}</strong>
                            <p>{subcategory.description || "No description"}</p>
                          </div>
                        </div>

                        <div className="settings-entity-meta">
                          <span className={`recommendation-status ${subcategory.isActive === false ? "is-inactive" : ""}`}>
                            {subcategory.isActive === false ? "Inactive" : "Active"}
                          </span>
                        </div>

                        <div className="settings-entity-actions">
                          <button type="button" onClick={() => openEdit("subcategory", subcategory)} className="edit-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/></svg>
                          </button>
                          <button type="button" className="delete" onClick={() => handleDeleteSubcategory(subcategory)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EntityModal
        show={Boolean(modalType)}
        title={modalTitle}
        type={modalType}
        categories={categories}
        selectedCategoryId={selectedCategoryId}
        submitLabel={editingItem ? "Save Changes" : modalType === "category" ? "Add Category" : "Add Subcategory"}
        formData={formData}
        errors={formErrors}
	        saving={saving}
	        onChange={handleChange}
	        onSelectedCategoryChange={handleParentCategoryChange}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default Settings;
